<template>
  <div v-if="images" :class="classes">
    <!-- Text + Images -->
    <template v-if="variant === 'topic' || variant === 'topic - reverse'">
      <collection-image v-if="mobile && mobileImage.src" :image="mobileImage" :class="['collection-spread-mobile-img', {'collection-spread-mobile-img--bleed': mobileImageBleed}]"></collection-image>  
      <section ref="stickyheader" :class="['collection-spread--column', 'collection-topic-text', {'sticky-header': images.length > 1}]">
        <div class="collection-spread--row">
          <h2 class="h2 uppercase">{{header}}</h2>
          <div class="h6 textbody justify-center" v-html="text"></div>
        </div>
      </section>
      <section v-if="!mobile" class="collection-spread--column">
        <div v-for="image in images" class="collection-spread--row">
          <collection-image :image="buildImgObj(image)"></collection-image>
        </div>
      </section>
    </template>
    <!-- Image Spread -->
    <template v-else>
      <section v-for="(image, index) in images" class="collection-spread--column" v-if="index < 2">
        <div class="collection-spread--row">
          <collection-image :image="buildImgObj(image)"></collection-image>  
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import Config from 'src/config'
import CollectionImage from 'components/CollectionImage'
import stickyHeaders from 'assets/js/sticky-headers'
import Prismic from 'prismic.io'
import getRespImgSrc from 'assets/js/getRespImgSrc'
export default {
  name: 'Spread',
  props: ['window', 'mobile', 'variant', 'header', 'text', 'imagesetId'],
  components: {
    CollectionImage
  },
  data () {
    return {
      imageset: null
    }
  },
  computed: {
    classes () {
      return {
        'collection-spread': true,
        'collection-topic': this.variant && this.variant.indexOf('topic') > -1,
        'collection-topic--static': this.variant && this.variant.indexOf('topic') > -1 && this.images.length < 2,
        'collection-topic--flipped': this.variant && this.variant.indexOf('reverse') > -1
      }
    },
    images () {
      return this.imageset ? this.imageset.getGroup('imageset.images').value : null
    },
    mobileImage () {
      return {
        type: null,
        src: this.imageset.getImage('imageset.mobile-image') ? getRespImgSrc(this.imageset, 'imageset.mobile-image', 'imageset-mobile') : null,
        caption: this.imageset.getStructuredText('imageset.mobile-image--caption') ? this.imageset.getStructuredText('imageset.mobile-image--caption').asHtml(Config.prismic.links) : ''
      }
    },
    mobileImageBleed () {
      return this.imageset.data['imageset.mobile-image--bleed'] ? this.imageset.getBoolean('imageset.mobile-image--bleed') : false
    }
  },
  methods: {
    getImageset () {
      if (this.imagesetId) {
        Prismic.api(Config.prismic.api_url).then((api) => {
          return api.getByID(this.imagesetId) // An empty query will return all the documents
        }).then((response) => {
          this.imageset = response
        }, function (err) {
          console.log('Error getting Imageset: ', err)
        })
      }
    },
    buildImgObj (image) { // image = prismic object
      let type = null
      let src = null
      // image
      if (image.getImage('image')) {
        src = getRespImgSrc(image, 'image')
      }
      // interactive image
      const interactiveImg = image.fragments['interactive-image']
      if (interactiveImg && interactiveImg.type === 'interactive-image') {
        type = 'image-360'
        src = interactiveImg.id
      }
      const img = {
        type: type,
        src: src,
        caption: image.data['caption'] ? image.getStructuredText('caption').asHtml(Config.prismic.links) : null,
        alt: image.data['caption'] ? image.getText('caption') : '',
        table: {
          header: image.getText('table--header'),
          text: image.data['table--text'] ? image.getStructuredText('table--text').asHtml(Config.prismic.links) : null
        }
      }
      return img
    },
    onScroll () {
      const sticky = this.$refs.stickyheader
      if (sticky && !window.Modernizr.cssstickyposition && window.innerWidth > this.window.bkpt.mobile) {
        stickyHeaders(sticky, window.pageYOffset, this.window.height)
      }
    }
  },
  created () {
    this.getImageset()
    window.addEventListener('scroll', this.onScroll)
  },
  destroyed () {
    window.removeEventListener('scroll', this.onScroll)
  }
}
</script>

<style lang="scss">
@import '../style/variables';
$spread-figure-w: 76.5625%;
$spread-leftCol-ml: 13.671875%;
$spread-rightCol-ml: 9.765625%;

.collection-spread{
  position:relative;
  @include clearFloat();
  // columns
  .collection-spread--column{
    width:50%;
    float:left;
    // left column
    &:first-child{
      .collection-spread--row{
        margin-left: $spread-leftCol-ml;
      }
    }
    // right column
    &:last-child{
      .collection-spread--row{
        margin-left: $spread-rightCol-ml;
      }
    }
  }
  // rows
  .collection-spread--row{
    position: relative;
    width:$spread-figure-w;
    // extra rows
    & + .collection-spread--row{
      padding-top: 0;
    }
    // last row
    &:last-of-type{
      margin-bottom: 0;
    }
  }

  // iamges
  img {
    width:100%;
  }

  // mobile
  @include on_mobile() {
    .collection-spread--column{
      position:static !important;
      width:100%;
      margin-left:0 !important;

      & + .collection-spread--column{
        margin-top: $collectionMobile-spreadGutter-ns;
      }

      .collection-spread--row{
        &, &:last-of-type{
          width:$collectionMobile-spreadFrameWidth;
          margin:0 auto;
          padding:0;
        }
      }
    }
  }
}

// topic
.collection-topic.collection-spread{

  // desktop
  @media (min-width:$bkpt-mobile+1) {
    // text pane
    .collection-spread--column:first-child{
      .collection-spread--row{
        height:100vh;
        display:flex;
        flex-direction:column;
        justify-content:space-between;
        padding-top:$collection-spreadPad-top;
      }
      h2{
        //margin-top: -.25em; // disable if using centering...
        margin-bottom: $gutter*2;
      }
      .textbody{
        margin-top: $gutter*2;
        padding-bottom:calc(#{$collection-spreadPad-bottom} - 0.3em);
      }
    }
    // images pane
    .collection-spread--column:last-child{
      @media (max-aspect-ratio:1140/760) {
        padding-top:$collection-spreadPad-top;
        // gutter between images
        .collection-spread--row{
          margin-bottom:$collection-spreadRowGutter;
          &:last-child{
            margin-bottom:$collection-spreadPad-bottom;
          }
        }  
      }
      @media (min-aspect-ratio:1140/760) {
        .collection-spread--row {
          min-height:100vh;
          display:flex;
          align-items:center;

          & + .collection-spread--row {
            margin-top: $collection-spreadRowGutter;
          } 
        }
      }
    }

    // flip columns
    &.collection-topic--flipped{
      .collection-spread--column{
        float:right;
        &:first-child{
          .collection-spread--row{
            margin-left: $spread-rightCol-ml;
          }
        }
        &:last-child{
          .collection-spread--row{
            margin-left: $spread-leftCol-ml;
          }
        }
      }
    }
    
    // sticky headers
    .no-csspositionsticky & {
      .sticky-header .collection-spread--row{
        height:100vh;
      }
      .sticky-header[data-sticky-header="fixed"] + section,
      .sticky-header[data-sticky-header="bottom"] + section{
        margin-left:50%;
      }
      &.collection-topic--flipped{
        .sticky-header{
          right:0;
        }
        .sticky-header[data-sticky-header="fixed"] + section,
        .sticky-header[data-sticky-header="bottom"] + section{
          margin-right:50%;
          margin-left:0;
        }
      }
    }

    // only one image
    &.collection-topic--static {
      display:flex;
      // text pane
      .collection-spread--column:first-child {
        display:flex;
        .collection-spread--row{
          height:auto;
        }
      }
      // redo flip columns with flex direction
      &.collection-topic--flipped {
        flex-direction:row-reverse;
      }
    }

    // captions hang from image
    .collection-spread--row{
      position:relative;
      figcaption{
        position:absolute;
        bottom:0; left:0;
        width:100%;
        transform: translateY(calc(100% + #{$gutter}));
      }
    }
  }
  // end desktop

  @include on_mobile() {
    // text pane
    .collection-spread--column.collection-topic-text .collection-spread--row{
      width:auto;
      padding:0 $collectionMobile-textMargin-ew;
    }
    h2{
      @include h5();
      margin-bottom: 1em;
    }
    .collection-spread-mobile-img{
      margin:0 auto;
      margin-bottom:$collectionMobile-blockMargin-ns;
      width:$collectionMobile-spreadFrameWidth;
      &.collection-spread-mobile-img--bleed{
        width:auto;
        padding:0 $gutter;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// Collection__Story__Spread.vue?bbb436fc